import { QueryClient, useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';

import { ENDPOINTS } from '@/api/endpoints';
import { fetchClient } from '@/api/fetch-client';
import {
  PatchReportError,
  PatchReportRequest,
  PatchReportResponse,
  ReportError,
  ReportRequest,
  ReportResponse,
} from '@/api/model';
import { removeEmptyParams } from '@/api/utils';

const KEY = {
  GET_REPORT_STUDY: 'GET_REPORT_STUDY',
} as const;

const request = {
  getReportStudy: async ({ payload: { params, studyId } }: ReportRequest, signal?: AbortSignal) => {
    const { data } = await fetchClient.FCT.get<ReportResponse>(
      `${ENDPOINTS.FCT.REPORT.STUDY}${studyId}`,
      { params: removeEmptyParams(params), signal },
    );

    return data;
  },
  patchReport: async ({ payload }: PatchReportRequest) => {
    const { data } = await fetchClient.FCT.patch<PatchReportResponse>(
      `${ENDPOINTS.FCT.REPORT.BASE}${payload.studyId}`,
      { body: payload },
    );

    return data;
  },
};

const query = {
  useGetReportStudy: (
    { payload }: ReportRequest,
    config?: Partial<UseQueryOptions<ReportResponse, ReportError>>,
  ) =>
    useQuery<ReportResponse, ReportError>({
      enabled: !!payload.params.study_sku,
      queryFn: ({ signal }) => request.getReportStudy({ payload }, signal),
      queryKey: [
        KEY.GET_REPORT_STUDY,
        payload.studyId,
        payload.params.user_id,
        payload.params.patient_gender,
      ],
      ...config,
    }),
};

const mutation = {
  usePatchReport: (queryClient: QueryClient) =>
    useMutation<PatchReportResponse, PatchReportError, PatchReportRequest>({
      mutationFn: request.patchReport,
      onSuccess: (value) => {
        queryClient.invalidateQueries({ queryKey: [KEY.GET_REPORT_STUDY, value.studyId] });

        return value;
      },
    }),
};

export const report = {
  KEY,
  mutation,
  query,
  request,
};
